/* ============================================================
   Движок интернационализации
   - хранит текущий язык (+ localStorage)
   - применяет переводы к статичной разметке (data-i18n / data-i18n-html)
   - оповещает подписчиков (динамические блоки, форма)
   ============================================================ */
import { ru } from './ru';
import { en } from './en';
import { fr } from './fr';
import type { Dictionary, Lang } from '../types';

export const dictionaries: Record<Lang, Dictionary> = { ru, en, fr };
export const LANGS: readonly Lang[] = ['ru', 'en', 'fr'];

const STORAGE_KEY = 'bs_lang';

type Listener = (dict: Dictionary, lang: Lang) => void;
const listeners = new Set<Listener>();

function readInitial(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en' || saved === 'fr') return saved;
  } catch {
    /* localStorage недоступен — игнорируем */
  }
  return 'ru';
}

let current: Lang = readInitial();

/** Текущий язык. */
export function getLang(): Lang {
  return current;
}

/** Словарь текущего языка. */
export function dict(): Dictionary {
  return dictionaries[current];
}

/** Подписаться на смену языка (вызывается и при первом applyLang). */
export function onLangChange(fn: Listener): void {
  listeners.add(fn);
}

/** Переключить язык: сохранить, обновить разметку, оповестить подписчиков. */
export function applyLang(lang: Lang): void {
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* игнорируем */
  }

  const d = dictionaries[lang];
  document.documentElement.lang = lang;
  document.title = d.doc_title;

  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n as keyof Dictionary | undefined;
    if (!key) return;
    const value = d[key];
    if (typeof value === 'string') el.textContent = value;
  });

  document.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html') as keyof Dictionary | null;
    if (!key) return;
    const value = d[key];
    if (typeof value === 'string') el.innerHTML = value;
  });

  document.querySelectorAll<HTMLButtonElement>('#lang [data-lang]').forEach((b) => {
    b.classList.toggle('on', b.dataset.lang === lang);
  });

  listeners.forEach((fn) => fn(d, lang));
}
